import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TableSortLabel,
  TextField,
  Box,
  CircularProgress,
  Pagination,
  Stack
} from '@mui/material'
import { Visibility, FileDownload } from '@mui/icons-material'
import { useState, useMemo } from 'react'
import { SellerSummary } from '../../services/sellerAnalyticsService'

interface SellersTableProps {
  sellers: SellerSummary[]
  loading?: boolean
  onViewDetails: (sellerId: string) => void
  onExport?: (sellerId: string) => void
}

type OrderBy = keyof SellerSummary
type Order = 'asc' | 'desc'

export default function SellersTable({
  sellers,
  loading = false,
  onViewDetails,
  onExport
}: SellersTableProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [orderBy, setOrderBy] = useState<OrderBy>('totalRevenue')
  const [order, setOrder] = useState<Order>('desc')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const getStatusColor = (
    status: 'excellent' | 'good' | 'average' | 'poor'
  ): 'success' | 'info' | 'warning' | 'error' => {
    switch (status) {
      case 'excellent':
        return 'success'
      case 'good':
        return 'info'
      case 'average':
        return 'warning'
      case 'poor':
        return 'error'
      default:
        return 'info'
    }
  }

  const getStatusLabel = (status: 'excellent' | 'good' | 'average' | 'poor'): string => {
    switch (status) {
      case 'excellent':
        return 'ممتاز'
      case 'good':
        return 'جيد'
      case 'average':
        return 'متوسط'
      case 'poor':
        return 'ضعيف'
      default:
        return status
    }
  }

  const filteredAndSortedSellers = useMemo(() => {
    let filtered = sellers.filter(seller =>
      seller.sellerName.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[orderBy]
      const bValue = b[orderBy]

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue
      }

      return 0
    })

    return filtered
  }, [sellers, searchTerm, orderBy, order])

  const paginatedSellers = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredAndSortedSellers.slice(startIndex, endIndex)
  }, [filteredAndSortedSellers, page, pageSize])

  const totalPages = Math.ceil(filteredAndSortedSellers.length / pageSize)

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="البحث عن تاجر..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </Box>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'sellerName'}
                  direction={orderBy === 'sellerName' ? order : 'asc'}
                  onClick={() => handleRequestSort('sellerName')}
                >
                  اسم التاجر
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'totalOffers'}
                  direction={orderBy === 'totalOffers' ? order : 'asc'}
                  onClick={() => handleRequestSort('totalOffers')}
                >
                  عدد العروض
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'acceptanceRate'}
                  direction={orderBy === 'acceptanceRate' ? order : 'asc'}
                  onClick={() => handleRequestSort('acceptanceRate')}
                >
                  معدل القبول
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'averageResponseTime'}
                  direction={orderBy === 'averageResponseTime' ? order : 'asc'}
                  onClick={() => handleRequestSort('averageResponseTime')}
                >
                  وقت الرد (دقيقة)
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'averageRating'}
                  direction={orderBy === 'averageRating' ? order : 'asc'}
                  onClick={() => handleRequestSort('averageRating')}
                >
                  التقييم
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'totalRevenue'}
                  direction={orderBy === 'totalRevenue' ? order : 'asc'}
                  onClick={() => handleRequestSort('totalRevenue')}
                >
                  الإيرادات
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">الحالة</TableCell>
              <TableCell align="center">الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSellers.map(seller => (
              <TableRow key={seller.sellerId} hover>
                <TableCell>{seller.sellerName}</TableCell>
                <TableCell align="center">{seller.totalOffers}</TableCell>
                <TableCell align="center">{seller.acceptanceRate.toFixed(1)}%</TableCell>
                <TableCell align="center">
                  {seller.averageResponseTime.toFixed(1)}
                </TableCell>
                <TableCell align="center">
                  {seller.averageRating.toFixed(2)} / 5.0
                </TableCell>
                <TableCell align="center">${seller.totalRevenue.toFixed(2)}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={getStatusLabel(seller.status)}
                    color={getStatusColor(seller.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="عرض التفاصيل">
                    <IconButton
                      size="small"
                      onClick={() => onViewDetails(seller.sellerId)}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  {onExport && (
                    <Tooltip title="تصدير">
                      <IconButton
                        size="small"
                        onClick={() => onExport(seller.sellerId)}
                      >
                        <FileDownload />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {paginatedSellers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  لا توجد بيانات
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ p: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="large"
          />
        </Stack>
      )}
    </Paper>
  )
}

